import {useState, useEffect} from "react"
import {Model} from "Model"
import cx from "classnames"
import {useTranslation} from "react-i18next"
import momentTz from "moment-timezone"
import {Row, Col, Checkbox, Switch, Tooltip} from "antd"
import moment from "moment"
import {cloneDeep, flatten, get, head, isEmpty, map, pick, size, uniq} from "lodash"
import debounce from "debounce-promise"
import {useHistory} from "react-router-dom"
import {useEdularModulesContext, Modules} from "@edular/modules"
import {Icon} from "components/Icon"
import {BaseEditor, BaseInput, BaseTextArea} from "components/inputs"
import {BaseButton, SecondaryButton} from "components"
import {KlassDropAsyncPaginate, KlassDropdown, TimezoneDropdown} from "components/Select"
import {BaseDatePicker, BaseTimePicker} from "components/DateTimePicker"
import {MonthlyRecurringBody, WeeklyRecurringBody, DailyRecurringBody} from "components/Calendar/Common/Recurring"
import {handleError, isInvalidValue, getFullName, translate, toastError, checkPermission} from "helpers"
import {
    createRruleFromEvent,
    getExtraEventData,
    getDisabledEndHours,
    getDisabledEndMinutes,
    getEndTimeByEventType
} from "helpers/calendar"
import {userServiceV3, eventTypeService, groupsService} from "services"
import {CreateEventPayload, CalendarRecurringType, RecurringEnd} from "types/calendar"
import {REMINDERS, OPTIONS_FREQUENCY as OPTIONS_FREQUENCY_ALL} from "sections/calendar/parts/common/constants"
import {EndTitle, EndBody} from "sections/calendar/parts/common"
import styles from "./CalendarEventForm.module.css"
import {DisplayMode} from "types/common"
import {Auth} from "types/auth"
import UserSelect from "components/UserSelect"
import WorkflowSelect from "components/WorkflowSelect"
import {WorkflowTriggerElement} from "types/workflow"
import {NewStudentStatusSelect} from "components/NewStudentStatusSelect"
import {useQuery} from "@tanstack/react-query"
import DepartmentSelect from "components/DepartmentSelect"
import {Permissions} from "types/permission"
import {LinkedCalendarInfo} from "types/calendar"

const OPTIONS_FREQUENCY = OPTIONS_FREQUENCY_ALL.map((opt) => ({
    value: opt.value,
    originalValue: opt.originalValue,
    label: translate(`calendar:calendar.frequency.${opt.label}`)
}))

const renderEventTypeOption = (option: Readonly<any>) => {
    return (
        <div className={styles.eventType}>
            <div className={styles.eventType__color} style={{backgroundColor: option.label}} />
            <div className={styles.eventType__label}>
                {option.name} ({option.code})
            </div>
        </div>
    )
}

type CalendarEventFormProps = {
    model: Model
    calendarId: number
    isShow?: boolean
    initialEventData: any
    disabledTypeEvents?: string[]
    onCreateSuccess: Function
    linkedCalendars: LinkedCalendarInfo[]
    onClose: () => void
    displayMode?: DisplayMode
}

const reminderOptions = REMINDERS.map((reminder) => ({value: reminder.id, label: reminder.name}))

const getReminderOptionsByIds = (ids: number[]) =>
    ids.flatMap((id) => {
        const option = reminderOptions.find((option) => option.value === id)
        return option ? [option] : []
    })

const defaultEventReminders = getReminderOptionsByIds([30 * 60, 2 * 60 * 60])

export const CalendarEventForm = (props: CalendarEventFormProps) => {
    const {
        isShow,
        onClose,
        initialEventData,
        calendarId,
        model,
        onCreateSuccess,
        displayMode = "normal",
        linkedCalendars
    } = props
    const [errors, setErrors] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [eventData, setEventData] = useState<any>({})
    const [selectedCalendarId, setSelectedCalendarId] = useState(calendarId)
    const history = useHistory()
    const {t} = useTranslation(["calendar", "common"])
    const isFullDisplay = displayMode === "normal"
    const {isModuleEnable} = useEdularModulesContext()
    const canInviteMultipleUsers = checkPermission({staff: [Permissions.Staff.Calendar.InviteMultipleUsers.Add]}, model)
    const calendarOptions = (linkedCalendars || []).filter(
        (calendar) => calendar.isPrimary || calendar.type === "group"
    )

    const {data: eventTypes} = useQuery(
        ["calendar-event-types"],
        async () => {
            const {data} = await eventTypeService.getAll({range: {limit: 100, offset: 0}})
            return data
        },
        {initialData: []}
    )

    useEffect(() => {
        setEventData({
            ...initialEventData,
            start_at_timezone: momentTz.tz.guess(),
            reminders: defaultEventReminders
        })
    }, [initialEventData])

    useEffect(() => {
        if (!isShow) return
        const handleKeyDown = (event) => {
            if (event.key === "Escape") onClose()
        }
        document.addEventListener("keydown", handleKeyDown)
        return () => {
            document.removeEventListener("keydown", handleKeyDown)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShow])

    const onGroupSearchChange = async (search = "", loadedOptions) => {
        try {
            const pageSize = 20
            const page = Math.ceil(loadedOptions.length / pageSize) + 1
            const {data, total} = await groupsService.getAll({
                range: {
                    pageSize,
                    page
                },
                filter: {
                    search
                }
            })
            const groups = data.map((g) => {
                return {
                    ...g,
                    name: `${g.name} (${g.members})`
                }
            })
            return {
                options: groups,
                hasMore: loadedOptions.length < total
            }
        } catch (error) {
            return {
                options: [],
                hasMore: false
            }
        }
    }

    const onChangeEventDataMultiple = (values: {[key: string]: any}) => {
        const newErrors = {...errors}
        const newEventData = cloneDeep(eventData)
        for (const key of Object.keys(values)) {
            const value = values[key]
            if (!isInvalidValue(value)) {
                delete newErrors[key]
            }
            newEventData[key] = value
            if (key === "startTime" && value) {
                const eventTypeDuration = eventData.eventType?.durationInMinutes
                const endTime = !!eventTypeDuration
                    ? moment(value).add(eventTypeDuration, "minutes")
                    : moment(value).add(1, "hour")
                const isSameDate = endTime.clone().startOf("date").isSame(moment(value).startOf("date"))
                newEventData.endTime = isSameDate ? endTime.clone() : moment(value).endOf("date")
            }
            if (key === "reminders" && isEmpty(value)) {
                newEventData.withSMS = false
                newEventData.withPushNotification = false
                newEventData.withEmail = false
            }
        }
        setErrors(newErrors)
        setEventData(newEventData)
    }

    const onChangeEventData = (key, value) => {
        onChangeEventDataMultiple({[key]: value})
    }

    const getMandatoryFields = (event) => {
        const mandatoryFields = ["name", "startTime", "endTime", "startDate", "recurring"]
        if (model.isStaffOrAdmin()) {
            mandatoryFields.push("eventType")
        }
        const recurring = typeof event.recurring === "string" ? event.recurring : event.recurring.originalValue
        switch (recurring) {
            case "YEARLY": {
                if (event.end?.value === RecurringEnd.AfterExecution) {
                    mandatoryFields.push("executions")
                } else if (event.end?.value === RecurringEnd.OnDate) {
                    mandatoryFields.push("endDate")
                }
                break
            }
            case "MONTHLY": {
                mandatoryFields.push("every")
                if (+event.monthlyRecurring === 0) {
                    /*day of the month*/
                    mandatoryFields.push("monthDay")
                } else {
                    /*day of the week*/
                    mandatoryFields.push("monthWeek", "monthWeekDay")
                }
                break
            }
            case "DAILY": {
                mandatoryFields.push("every")
                break
            }
            case "WEEKLY": {
                mandatoryFields.push("every", "days")
                break
            }
        }
        if (recurring !== "NEVER") {
            mandatoryFields.push("end")
            if (event.end?.value === RecurringEnd.AfterExecution) {
                mandatoryFields.push("executions")
            } else if (event.end?.value === RecurringEnd.OnDate) {
                mandatoryFields.push("endDate")
            }
        }
        return mandatoryFields
    }

    const validate = () => {
        const event = getExtraEventData(eventData)
        const mandatoryFields = getMandatoryFields(event)
        const errorsAux: any = {}
        mandatoryFields.forEach((field) => {
            if (isInvalidValue(event[field])) {
                errorsAux[field] = true
            }
        })
        const hasRequiredFieldError = Object.keys(errorsAux).length > 0
        if (hasRequiredFieldError) {
            setErrors(errorsAux)
            toastError(t("common:validation.fieldRequired"))
            return true
        }
        if (moment(event.startTime).isSameOrAfter(moment(event.endTime))) {
            errorsAux.startTime = true
            errorsAux.endTime = true
            toastError(t("message.endTimeGreaterStartTime"))
            return true
        }
        const {reminders, withPushNotification, withSMS, withEmail} = eventData
        if (!isEmpty(reminders) && !withPushNotification && !withSMS && !withEmail) {
            toastError(t("message.selectReminderMethod"))
            return true
        }
        return false
    }

    const setDateOfTimeValue = (timeValue) => {
        const startDate = moment(eventData.startDate)
        const date = startDate.get("date")
        const month = startDate.get("month")
        const year = startDate.get("year")
        return moment(timeValue).set("date", date).set("month", month).set("year", year)
    }

    const handleOnSubmit = async () => {
        if (isSubmitting) {
            return
        }
        const newEventData = {...eventData}
        eventData.startTime = setDateOfTimeValue(eventData.startTime)
        eventData.endTime = setDateOfTimeValue(eventData.endTime)
        setEventData(newEventData)
        const hasError = validate()
        if (hasError) {
            return
        }
        await createEvent()
    }

    const getDefaultEventType = () => {
        return eventTypes.find((item) => item.isDefault) || head(eventTypes)
    }

    const getDataSubmit = () => {
        const {
            name,
            allDay,
            description,
            eventType,
            startDate,
            startTime,
            endTime,
            invitedUsers,
            group,
            reminders,
            withSMS,
            withPushNotification,
            withInvitePushNotification,
            rsvpSettings,
            withInviteEmail,
            withEmail,
            eventSignInRequired,
            eventTrackAttendanceMinutes,
            attendedChangeToStatus,
            notAttendedChangeToStatus,
            completionWorkflow,
            hostUsers,
            address,
            department
        } = eventData
        const eventTypeData = model.isStaffOrAdmin() ? eventType : getDefaultEventType()
        const ownerUserId = model.viewOtherUserCalendar?.id ? model.viewOtherUserCalendar?.id : +model.user.id
        const rrule = createRruleFromEvent(eventData)
        const startTimeUpdated = `${moment(startDate).format("YYYY-MM-DD")} ${moment(startTime).format("HH:mm:ss")}`
        const endTimeUpdated = `${moment(startDate).format("YYYY-MM-DD")} ${moment(endTime).format("HH:mm:ss")}`
        const duration = allDay ? 3600 * 24 : moment(endTimeUpdated).diff(moment(startTimeUpdated), "seconds")
        const invited_user_ids = uniq(map(invitedUsers, (user) => user.userId))
        const payload: CreateEventPayload = {
            calendar_id: selectedCalendarId,
            type: "event",
            name,
            description: description || "",
            duration,
            owner_user_id: ownerUserId,
            rrule,
            start_at_timezone: eventData.start_at_timezone.value || momentTz.tz.guess(),
            host_user_ids: uniq(map(hostUsers, (user) => user.id)),
            start_at_wall: allDay
                ? moment(startDate).startOf("day").format("YYYY-MM-DD HH:mm:ss")
                : `${moment(startDate).format("YYYY-MM-DD")} ${moment(startTime).format("HH:mm:ss")}`,
            json_data: {
                eventType: `${eventTypeData.name} (${eventTypeData.code})`,
                eventTypeId: eventTypeData.eventTypeId,
                color: eventTypeData.label,
                allDay,
                notifications: reminders.map((reminder) => reminder.value),
                with_sms: withSMS,
                with_push_notification: withPushNotification,
                with_invite_push_notification: withInvitePushNotification,
                without_rsvp: !rsvpSettings,
                with_invite_email: withInviteEmail,
                with_email: withEmail,
                eventSignInRequired,
                eventTrackAttendanceMinutes,
                attendedChangeToStatus,
                notAttendedChangeToStatus,
                completionWorkflow,
                address,
                departmentId: department?.departmentId || undefined,
                department: department?.departmentId ? department : undefined
            }
        }
        if (invited_user_ids.length) {
            payload.invited_user_ids = invited_user_ids
        }
        if (group) {
            payload.edular_group_id = group.groupId
        }
        return payload
    }

    const createEvent = async () => {
        try {
            setIsSubmitting(true)
            const payload = getDataSubmit()
            await model.addEvent(payload)
            onClose()
            if (onCreateSuccess) {
                onCreateSuccess()
            }
        } catch (error) {
            handleError(error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const getStaffs = async (search = "", loadedOptions) => {
        try {
            const {data, total} = await userServiceV3.getAll({
                range: {
                    limit: 20,
                    offset: loadedOptions.length
                },
                filter: {
                    type: [Auth.UserProfileType.Staff],
                    active: 1,
                    search
                },
                linkedObjects: true
            })
            return {
                options: data,
                hasMore: loadedOptions.length < total
            }
        } catch (error) {
            return {
                options: [],
                hasMore: false
            }
        }
    }

    const renderInviteOption = (user) => {
        const type = get(user, "profiles[0].type", "")
        return (
            <div>
                <span className={styles.userId}>{user.customUserId}</span>
                {getFullName(user)} <span className={styles.userType}>({type})</span>
            </div>
        )
    }

    const onClickMoreDetail = () => {
        const {name, description, eventType, startTime, endTime, invitedUsers, group} = eventData
        history.push({
            pathname: "/calendar-event/detail",
            state: {
                eventData: {
                    name,
                    calendarId: selectedCalendarId,
                    description,
                    eventType,
                    invitedUsers,
                    group,
                    timezone: momentTz.tz.guess(),
                    startTime: moment(startTime).format(),
                    endTime: moment(endTime).format()
                }
            }
        })
    }

    const renderEndBody = () => {
        return (
            <EndBody
                end={eventData.end}
                endDate={eventData.endDate}
                executions={eventData.executions}
                errors={errors}
                setEnd={(newValue) => onChangeEventData("end", newValue)}
                setEndDate={(newValue) => onChangeEventData("endDate", newValue)}
                setExecutions={(newValue) => onChangeEventData("executions", newValue)}
            />
        )
    }

    const renderYearlyRecurring = () => {
        return (
            <>
                <Col span={2} />
                <Col span={6}>
                    <EndTitle />
                </Col>
                <Col span={isFullDisplay ? 16 : 24}>{renderEndBody()}</Col>
            </>
        )
    }

    const renderMonthlyRecurring = () => {
        return (
            <>
                <Col span={8} />
                <Col span={isFullDisplay ? 16 : 24}>
                    <MonthlyRecurringBody eventData={eventData} errors={errors} onChangeEventData={onChangeEventData} />
                </Col>
                <Col span={2} />
                <Col span={6}>
                    <EndTitle />
                </Col>
                <Col span={isFullDisplay ? 16 : 24}>{renderEndBody()}</Col>
            </>
        )
    }

    const renderWeeklyRecurring = () => {
        return (
            <>
                <Col span={8} />
                <Col span={isFullDisplay ? 16 : 24}>
                    <WeeklyRecurringBody eventData={eventData} errors={errors} onChangeEventData={onChangeEventData} />
                </Col>
                <Col span={2} />
                <Col span={6}>
                    <EndTitle />
                </Col>
                <Col span={isFullDisplay ? 16 : 24}>{renderEndBody()}</Col>
            </>
        )
    }

    const renderDailyRecurring = () => {
        return (
            <>
                <Col span={8} />
                <Col span={isFullDisplay ? 16 : 24}>
                    <DailyRecurringBody eventData={eventData} errors={errors} onChangeEventData={onChangeEventData} />
                </Col>
                <Col span={2} />
                <Col span={6}>
                    <EndTitle />
                </Col>
                <Col span={isFullDisplay ? 16 : 24}>{renderEndBody()}</Col>
            </>
        )
    }

    const renderRecurring = () => {
        const originalValue = eventData.recurring?.originalValue
        switch (originalValue) {
            case CalendarRecurringType.Yearly:
                return renderYearlyRecurring()
            case CalendarRecurringType.Monthly:
                return renderMonthlyRecurring()
            case CalendarRecurringType.Weekly:
                return renderWeeklyRecurring()
            case CalendarRecurringType.Daily:
                return renderDailyRecurring()
            default:
                return null
        }
    }

    const renderAction = () => {
        if (isFullDisplay) {
            return (
                <div className={styles.actionBtn}>
                    <SecondaryButton title="More detail" onClick={onClickMoreDetail} />
                    <div className={styles.rightActionBtn}>
                        <SecondaryButton
                            title={t("common:action.cancel")}
                            onClick={onClose}
                            disabled={isSubmitting}
                            className={styles.cancelBtn}
                        />
                        <BaseButton title={t("common:action.save")} onClick={handleOnSubmit} loading={isSubmitting} />
                    </div>
                </div>
            )
        } else {
            return (
                <div className={styles.actionBtnPanel}>
                    <BaseButton
                        title="Create event"
                        onClick={handleOnSubmit}
                        loading={isSubmitting}
                        className={styles.saveBtn}
                    />
                </div>
            )
        }
    }

    const renderAllDayField = () => {
        if (isFullDisplay) {
            return (
                <Row gutter={[16, 6]} className={styles.rowItem}>
                    <Col span={2} className={styles.eventPopupLabel} />
                    <Col span={6} className={styles.eventPopupLabel}>
                        <label>{t("calendar.wholeDay")}</label>
                    </Col>
                    <Col span={16}>
                        <Switch
                            size="small"
                            onClick={(v) => onChangeEventData("allDay", v)}
                            checked={eventData.allDay}
                        />
                    </Col>
                </Row>
            )
        } else {
            return (
                <Row gutter={[16, 6]} className={styles.rowItem}>
                    <div className={styles.checkboxWrap}>
                        <div
                            className={cx(styles.checkbox, {[styles.checked]: eventData.allDay})}
                            onClick={() => onChangeEventData("allDay", !eventData.allDay)}>
                            <Checkbox checked={eventData.allDay} />
                            <span className={styles.checkboxTitle}>{t("calendar.wholeDay")}</span>
                        </div>
                    </div>
                </Row>
            )
        }
    }

    const renderDepartmentField = () => {
        if (!model.isStaffOrAdmin()) {
            return null
        }
        if (isFullDisplay) {
            return (
                <Row gutter={[16, 6]} className={styles.rowItem}>
                    <Col span={2} className={styles.eventPopupLabel}>
                        <Icon icon="DOOR_OPEN" className={styles.eventPopupIcon} color="#62B1FF" />
                    </Col>
                    <Col span={6} className={styles.eventPopupLabel}>
                        <label>Department</label>
                    </Col>
                    <Col span={16}>
                        <DepartmentSelect
                            onChange={(newValue) => onChangeEventData("department", newValue)}
                            placeholder="Select"
                            value={eventData.department}
                            isClearable
                            isDisabled={!isEmpty(eventData.eventType?.department)}
                        />
                    </Col>
                </Row>
            )
        }
        return (
            <Row gutter={[16, 6]} className={styles.rowItem}>
                <DepartmentSelect
                    onChange={(newValue) => onChangeEventData("department", newValue)}
                    placeholder="Departments"
                    value={eventData.department}
                    isClearable
                    isDisabled={!isEmpty(eventData.eventType?.department)}
                />
            </Row>
        )
    }

    const debounceStaffs = debounce(getStaffs, 300)
    const onGroupSearchChangeDebounce = debounce(onGroupSearchChange, 300)
    const isStaffOrAdmin = model.isStaffOrAdmin()
    const campusIds = flatten(
        uniq(
            (model.user?.profiles || []).map((profile) => profile?.campuses?.map((campus) => campus.id)).filter(Boolean)
        )
    )

    return (
        <div className={styles.eventPopupWrap}>
            {isFullDisplay && (
                <div className={styles.eventPopupHeader}>
                    <h1 className={styles.eventPopupTitle}>{t("calendar.createEvent")}</h1>
                </div>
            )}
            <div className={cx(styles.body, {[styles.bodyPanel]: !isFullDisplay})}>
                {size(calendarOptions) > 1 && (
                    <Row gutter={[16, 6]} className={styles.rowItem}>
                        {isFullDisplay && (
                            <Col span={2} className={styles.eventPopupLabel}>
                                <Icon icon="CALENDAR" className={styles.eventPopupIcon} color="#62B1FF" />
                            </Col>
                        )}
                        <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                            <label>Calendar</label>
                        </Col>
                        <Col span={isFullDisplay ? 16 : 24}>
                            <KlassDropdown
                                options={calendarOptions}
                                valueKey="calendarId"
                                value={calendarOptions.find((calendar) => calendar.calendarId === selectedCalendarId)}
                                onChange={(newValue) => setSelectedCalendarId(newValue.calendarId)}
                                placeholder={t("calendar.calendar")}
                            />
                        </Col>
                    </Row>
                )}
                <Row gutter={[16, 6]} className={styles.rowItem}>
                    {isFullDisplay && (
                        <Col span={2} className={styles.eventPopupLabel}>
                            <Icon icon="PENCIL" className={styles.eventPopupIcon} color="#62B1FF" />
                        </Col>
                    )}
                    <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                        <label>{t("calendar.workSchedule.name")}</label>
                    </Col>
                    <Col span={isFullDisplay ? 16 : 24}>
                        <BaseInput
                            onChange={(newValue) => onChangeEventData("name", newValue)}
                            placeholder={t("calendar.workSchedule.name")}
                            value={eventData.name}
                            className={cx(!!errors["name"] ? styles.error : "")}
                        />
                    </Col>
                </Row>
                {model.isStaffOrAdmin() && (
                    <Row gutter={[16, 6]} className={styles.rowItem}>
                        {isFullDisplay && (
                            <Col span={2} className={styles.eventPopupLabel}>
                                <Icon icon="OUTLINE" className={styles.eventPopupIcon} color="#62B1FF" />
                            </Col>
                        )}
                        <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                            <label>{t("calendar.type")}</label>
                        </Col>
                        <Col span={isFullDisplay ? 16 : 24}>
                            <KlassDropdown
                                onChange={(newValue) => {
                                    const newEventData: any = {eventType: newValue}
                                    if (!eventData.object_id) {
                                        newEventData.eventSignInRequired = !!newValue?.signInRequired
                                        newEventData.eventTrackAttendanceMinutes = !!newValue?.trackAttendanceMinutes
                                    }
                                    newEventData.description = newValue?.notes || ""
                                    newEventData.department = newValue?.department
                                    if (eventData.startTime) {
                                        eventData.endTime = getEndTimeByEventType(
                                            eventData.startTime,
                                            newValue?.durationInMinutes
                                        )
                                    }
                                    onChangeEventDataMultiple(newEventData)
                                }}
                                options={eventTypes}
                                value={eventData.eventType}
                                placeholder={t("calendar.type")}
                                getOptionLabel={renderEventTypeOption}
                                valueKey="eventTypeId"
                                error={errors["eventType"]}
                            />
                        </Col>
                    </Row>
                )}
                {renderDepartmentField()}
                {renderAllDayField()}
                <Row gutter={[16, 6]} className={styles.rowItem}>
                    {isFullDisplay && (
                        <Col span={2} className={styles.eventPopupLabel}>
                            <Icon icon="CALENDAR_FILL" className={styles.eventPopupIcon} color="#62B1FF" />
                        </Col>
                    )}
                    <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                        <label>{t("calendar.date")}</label>
                    </Col>
                    <Col span={isFullDisplay ? 16 : 24}>
                        <BaseDatePicker
                            onChange={(newValue) => onChangeEventData("startDate", newValue)}
                            value={eventData.startDate ? moment(eventData.startDate) : ""}
                            className={cx(styles.shadow, !!errors["startDate"] ? styles.error : "")}
                        />
                    </Col>
                </Row>
                {!eventData.allDay && (
                    <>
                        <Row gutter={[16, 6]} className={styles.rowItem}>
                            {isFullDisplay && (
                                <Col span={2} className={styles.eventPopupLabel}>
                                    <Icon icon="TIME_CLOCK_OUTLINE" className={styles.eventPopupIcon} color="#62B1FF" />
                                </Col>
                            )}
                            <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                                <label>{t("calendar.time")}</label>
                            </Col>
                            <Col span={isFullDisplay ? 8 : 12}>
                                <BaseTimePicker
                                    value={eventData.startTime}
                                    placeholder="Time"
                                    format={model.user.timeFormat === Auth.TimeFormat.Time_12 ? "hh:mm A" : "HH:mm"}
                                    onChange={(newValue) => onChangeEventData("startTime", newValue)}
                                    error={errors["startTime"]}
                                />
                            </Col>
                            <Col span={isFullDisplay ? 8 : 12}>
                                <BaseTimePicker
                                    value={eventData.endTime}
                                    placeholder="Time"
                                    format={model.user.timeFormat === Auth.TimeFormat.Time_12 ? "hh:mm A" : "HH:mm"}
                                    onChange={(newValue) => onChangeEventData("endTime", newValue)}
                                    error={errors["endTime"]}
                                    disabledHours={() => getDisabledEndHours(eventData.startTime)}
                                    disabledMinutes={() =>
                                        getDisabledEndMinutes(eventData.startTime, eventData.endTime)
                                    }
                                />
                            </Col>
                        </Row>
                        <Row gutter={[16, 6]} className={styles.rowItem}>
                            {isFullDisplay && (
                                <Col span={2} className={styles.eventPopupLabel}>
                                    <Icon icon="GLOBE" className={styles.eventPopupIcon} color="#62B1FF" />
                                </Col>
                            )}
                            <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                                <label>{t("calendar.timezone")}</label>
                            </Col>
                            <Col span={isFullDisplay ? 16 : 24}>
                                <TimezoneDropdown
                                    onChange={(newValue) => onChangeEventData("start_at_timezone", newValue)}
                                    value={eventData.start_at_timezone || ""}
                                    placeholder={t("common:selectField.placeholder")}
                                />
                            </Col>
                        </Row>
                    </>
                )}
                {isStaffOrAdmin && (
                    <>
                        <Row gutter={[16, 6]} className={styles.rowItem}>
                            {isFullDisplay && (
                                <Col span={2} className={styles.eventPopupLabel}>
                                    <Icon icon="PERSON_FILL" className={styles.eventPopupIcon} color="#62B1FF" />
                                </Col>
                            )}
                            <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                                <label>{t("calendar.group")}</label>
                            </Col>

                            <Col span={isFullDisplay ? 16 : 24}>
                                <KlassDropAsyncPaginate
                                    onChange={(newValue) => onChangeEventData("group", newValue)}
                                    loadOptions={onGroupSearchChangeDebounce}
                                    value={eventData.group}
                                    placeholder={t("calendar.addGroup")}
                                    valueKey="groupId"
                                    error={errors["group"]}
                                    isClearable
                                />
                            </Col>
                        </Row>
                        <Row gutter={[16, 6]} className={styles.rowItem}>
                            {isFullDisplay && (
                                <Col span={2} className={styles.eventPopupLabel}>
                                    <Icon icon="PERSON_ADD" className={styles.eventPopupIcon} color="#62B1FF" />
                                </Col>
                            )}
                            <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                                <label>{t("calendar.hosts")}</label>
                            </Col>
                            <Col span={isFullDisplay ? 16 : 24}>
                                <div className={styles.hostsWrap}>
                                    <div className={styles.hostsDropdown}>
                                        <KlassDropAsyncPaginate
                                            loadOptions={debounceStaffs}
                                            getOptionLabel={(option) => getFullName(option)}
                                            onChange={(newValue, actionMeta) => {
                                                if (
                                                    ["remove-value", "pop-value"].includes(actionMeta.action) &&
                                                    actionMeta.removedValue.isFixed
                                                ) {
                                                    return
                                                }
                                                onChangeEventData("hostUsers", newValue)
                                            }}
                                            placeholder="Select"
                                            value={eventData.hostUsers}
                                            error={!!errors["hostUsers"]}
                                            isMulti
                                            stylesCustom={{
                                                multiValueRemove: (base, state) =>
                                                    state.data.isFixed
                                                        ? {...base, display: "none"}
                                                        : {...base, color: "var(--whitebase)"}
                                            }}
                                        />
                                    </div>
                                    <Tooltip title="Hosts can edit and delete the event">
                                        <div className={styles.infoIconWrap}>
                                            <Icon icon="INFO" />
                                        </div>
                                    </Tooltip>
                                </div>
                            </Col>
                        </Row>
                        <Row gutter={[16, 6]} className={styles.rowItem}>
                            {isFullDisplay && (
                                <Col span={2} className={styles.eventPopupLabel}>
                                    <Icon icon="PERSON_ADD" className={styles.eventPopupIcon} color="#62B1FF" />
                                </Col>
                            )}
                            <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                                <label>{t("calendar.invite")}</label>
                            </Col>
                            <Col span={isFullDisplay ? 16 : 24}>
                                <UserSelect
                                    isMulti={canInviteMultipleUsers}
                                    placeholder={t("calendar.contactName")}
                                    isActive={eventData.includeArchive ? [true, false] : true}
                                    getOptionLabel={(option) => renderInviteOption(option)}
                                    value={
                                        canInviteMultipleUsers ? eventData.invitedUsers : head(eventData.invitedUsers)
                                    }
                                    onChange={(value) => {
                                        if (canInviteMultipleUsers) {
                                            onChangeEventData("invitedUsers", value ?? [])
                                        } else {
                                            onChangeEventData("invitedUsers", value ? [value] : [])
                                        }
                                    }}
                                    className={cx(!!errors["invitedUsers"] ? styles.error : "")}
                                    type={
                                        !model.isStaffOrAdmin()
                                            ? [Auth.UserProfileType.Staff, Auth.UserProfileType.Others]
                                            : undefined
                                    }
                                    campusIds={campusIds}
                                    isClearable
                                />
                            </Col>
                            {isFullDisplay && <Col span={8} />}
                            <Col span={isFullDisplay ? 16 : 24}>
                                <div>
                                    <Checkbox
                                        checked={!!eventData.withInvitePushNotification}
                                        onChange={(e) =>
                                            onChangeEventData("withInvitePushNotification", e.target.checked)
                                        }>
                                        {t("calendar.sendInvitePushNotification")}
                                    </Checkbox>
                                </div>
                                <div>
                                    <Checkbox
                                        checked={!!eventData.withInviteEmail}
                                        onChange={(e) => onChangeEventData("withInviteEmail", e.target.checked)}>
                                        {t("calendar.sendInviteEmail")}
                                    </Checkbox>
                                </div>
                                <div>
                                    <Checkbox
                                        checked={!!eventData.rsvpSettings}
                                        onChange={(e) => onChangeEventData("rsvpSettings", e.target.checked)}>
                                        {t("calendar.rsvp")}
                                    </Checkbox>
                                </div>
                                <Checkbox
                                    checked={!!eventData.includeArchive}
                                    onChange={(e) => onChangeEventData("includeArchive", e.target.checked)}>
                                    {t("calendar.searchArchive")}
                                </Checkbox>
                            </Col>
                        </Row>
                    </>
                )}
                <Row gutter={[16, 12]} className={styles.rowItem}>
                    {isFullDisplay && (
                        <Col span={2} className={styles.eventPopupLabel}>
                            <Icon icon="REPEAT" className={styles.eventPopupIcon} color="#62B1FF" />
                        </Col>
                    )}
                    <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                        <label>{t("calendar.recurring")}</label>
                    </Col>
                    <Col span={isFullDisplay ? 16 : 24}>
                        <div className={styles.dropDown}>
                            <KlassDropdown
                                onChange={(newValue) => onChangeEventData("recurring", newValue)}
                                options={OPTIONS_FREQUENCY}
                                value={eventData.recurring}
                                labelKey="label"
                                valueKey="value"
                                placeholder={t("calendar.recurring")}
                            />
                        </div>
                    </Col>
                    {renderRecurring()}
                </Row>

                {/*renderPrivateField()*/}

                <Row gutter={[16, 12]} className={styles.rowItem}>
                    {isFullDisplay && (
                        <Col span={2} className={styles.eventPopupLabel}>
                            <Icon icon="NOTIFY" className={styles.eventPopupIcon} color="#62B1FF" />
                        </Col>
                    )}
                    <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                        <label>{t("calendar.reminder")}</label>
                    </Col>
                    <Col span={isFullDisplay ? 16 : 24}>
                        <KlassDropdown
                            creatable
                            isMulti
                            isClearable
                            placeholder="Select or type reminder"
                            formatCreateLabel={(inputValue) => `Use "${inputValue} minutes"`}
                            options={reminderOptions}
                            valueKey="value"
                            labelKey="label"
                            onChange={(options) => {
                                const newOptions =
                                    map(options, (option) =>
                                        option.__isNew__
                                            ? isNaN(option.value)
                                                ? null
                                                : {value: +option.value * 60, label: `${option.value} minutes before`}
                                            : option
                                    ).filter(Boolean) ?? []
                                onChangeEventData("reminders", newOptions)
                            }}
                            value={eventData.reminders}
                            error={!!errors["reminders"]}
                        />
                    </Col>
                    {isFullDisplay && <Col span={8} />}
                    <Col span={isFullDisplay ? 16 : 24}>
                        <div className={styles.reminderMethods}>
                            <div>
                                <Checkbox
                                    checked={!!eventData.withPushNotification}
                                    disabled={isEmpty(eventData.reminders)}
                                    onChange={(e) => onChangeEventData("withPushNotification", e.target.checked)}>
                                    {t("calendar.sendMobilePushNotificationReminder")}
                                </Checkbox>
                            </div>
                            {isModuleEnable(Modules.Sms) && (
                                <div>
                                    <Checkbox
                                        checked={!!eventData.withSMS}
                                        disabled={isEmpty(eventData.reminders)}
                                        onChange={(e) => onChangeEventData("withSMS", e.target.checked)}>
                                        {t("calendar.sendSmsReminder")}
                                    </Checkbox>
                                </div>
                            )}
                            <div>
                                <Checkbox
                                    checked={!!eventData.withEmail}
                                    disabled={isEmpty(eventData.reminders)}
                                    onChange={(e) => onChangeEventData("withEmail", e.target.checked)}>
                                    {t("calendar.sendEmailReminder")}
                                </Checkbox>
                            </div>
                        </div>
                    </Col>
                </Row>
                <Row gutter={[16, 6]} className={styles.rowItem}>
                    {isFullDisplay && (
                        <Col span={2} className={styles.eventPopupLabel}>
                            <Icon icon="NOTES" className={styles.eventPopupIcon} color="#62B1FF" />
                        </Col>
                    )}
                    <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                        <label>{t("calendar.notes")}</label>
                    </Col>
                    <Col span={isFullDisplay ? 16 : 24}>
                        <BaseEditor
                            onChange={(newValue) => onChangeEventData("description", newValue)}
                            placeholder={t("calendar.notes")}
                            value={eventData.description}
                        />
                    </Col>
                </Row>

                <Row gutter={[16, 6]} className={styles.rowItem}>
                    {isFullDisplay && (
                        <Col span={2} className={styles.eventPopupLabel}>
                            <Icon icon="LOCATION" className={styles.eventPopupIcon} color="#62B1FF" />
                        </Col>
                    )}
                    <Col span={isFullDisplay ? 6 : 24} className={styles.eventPopupLabel}>
                        <label>Address</label>
                    </Col>
                    <Col span={isFullDisplay ? 16 : 24}>
                        <BaseTextArea
                            onChange={(newValue) => onChangeEventData("address", newValue)}
                            placeholder="Address"
                            value={eventData.address}
                            className={cx(!!errors["address"] ? styles.error : "")}
                        />
                    </Col>
                </Row>
                {isStaffOrAdmin && (
                    <Row gutter={[16, 12]} className={styles.rowItem}>
                        {isFullDisplay && <Col span={8} />}
                        <Col span={isFullDisplay ? 16 : 24}>
                            <Checkbox
                                checked={!!eventData.eventSignInRequired}
                                onChange={(e) => onChangeEventData("eventSignInRequired", e.target.checked)}>
                                Sign-in Required
                            </Checkbox>
                        </Col>
                        {!!eventData.eventSignInRequired && (
                            <>
                                {isFullDisplay && <Col span={8} />}
                                <Col span={isFullDisplay ? 16 : 24}>
                                    <Checkbox
                                        checked={!!eventData.eventTrackAttendanceMinutes}
                                        onChange={(e) =>
                                            onChangeEventData("eventTrackAttendanceMinutes", e.target.checked)
                                        }>
                                        Track attendance minutes
                                    </Checkbox>
                                </Col>
                                <Col span={isFullDisplay ? 8 : 24}>
                                    <label>For individuals that attended change status to</label>
                                </Col>
                                <Col span={isFullDisplay ? 16 : 24}>
                                    <NewStudentStatusSelect
                                        value={eventData.attendedChangeToStatus}
                                        onChange={(newValue) => onChangeEventData("attendedChangeToStatus", newValue)}
                                    />
                                </Col>
                                <Col span={isFullDisplay ? 8 : 24}>
                                    <label>For individuals that didn’t attended change status to</label>
                                </Col>
                                <Col span={isFullDisplay ? 16 : 24}>
                                    <NewStudentStatusSelect
                                        value={eventData.notAttendedChangeToStatus}
                                        onChange={(newValue) =>
                                            onChangeEventData("notAttendedChangeToStatus", newValue)
                                        }
                                    />
                                </Col>
                                <Col span={isFullDisplay ? 8 : 24}>
                                    <label>Trigger workflow upon completion (for attendees)</label>
                                </Col>
                                <Col span={isFullDisplay ? 16 : 24}>
                                    <WorkflowSelect
                                        isClearable
                                        triggerElement={WorkflowTriggerElement.CalendarEventCompletion}
                                        value={eventData.completionWorkflow}
                                        onChange={(newValue) =>
                                            onChangeEventData(
                                                "completionWorkflow",
                                                newValue ? pick(newValue, ["id", "code", "name"]) : undefined
                                            )
                                        }
                                    />
                                </Col>
                            </>
                        )}
                    </Row>
                )}
            </div>
            {renderAction()}
        </div>
    )
}
